import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import axios from "axios";

const WeekSelector = ({ selectedWeek, setSelectedWeek, className, availableWeeksOnly }) => {
  const { t } = useTranslation();
  const [weeks, setWeeks] = useState([]);

  // Функція для знаходження першого понеділка року
  const getFirstMonday = (year) => {
    const firstMonday = new Date(year, 0, 1);
    while (firstMonday.getDay() !== 1) {
      firstMonday.setDate(firstMonday.getDate() + 1);
    }
    return firstMonday;
  };

  // З номера тижня формату "202501" отримати понеділок тижня
  const getMondayFromWeekNumber = (formattedWeekNumber) => {
    const str = String(formattedWeekNumber);
    const year = parseInt(str.slice(0, 4), 10);
    const week = parseInt(str.slice(4), 10);
    const firstMonday = getFirstMonday(year);
    const monday = new Date(firstMonday);
    monday.setDate(firstMonday.getDate() + (week - 1) * 7);
    return monday;
  };

  // Функція для отримання номера тижня та року для заданої дати
  const getWeekInfo = (date) => {
    const year = date.getFullYear();
    const firstMonday = getFirstMonday(year);
    
    // Знаходимо понеділок тижня для заданої дати
    const dayOfWeek = date.getDay();
    const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const mondayOfWeek = new Date(date);
    mondayOfWeek.setDate(date.getDate() - daysToMonday);
    
    // Якщо понеділок тижня знаходиться в попередньому році
    if (mondayOfWeek.getFullYear() < year) {
      const prevYear = year - 1;
      const prevFirstMonday = getFirstMonday(prevYear);
      const daysDiff = Math.floor((mondayOfWeek - prevFirstMonday) / (1000 * 60 * 60 * 24));
      const weekNumber = Math.floor(daysDiff / 7) + 1;
      return { year: prevYear, week: weekNumber, monday: mondayOfWeek };
    }
    
    // Обчислюємо номер тижня
    const daysDiff = Math.floor((mondayOfWeek - firstMonday) / (1000 * 60 * 60 * 24));
    const weekNumber = Math.floor(daysDiff / 7) + 1;
    
    return { year, week: weekNumber, monday: mondayOfWeek };
  };

  useEffect(() => {
    if (availableWeeksOnly) {
      axios.get("/api/available-source-weeks")
        .then((res) => {
          const weekNumbers = res.data || [];
          const tempWeeks = weekNumbers.map((formattedWeekNumber) => {
            const monday = getMondayFromWeekNumber(formattedWeekNumber);
            const weekInfo = getWeekInfo(monday);
            const startOfWeek = new Date(weekInfo.monday);
            const endOfWeek = new Date(weekInfo.monday);
            endOfWeek.setDate(endOfWeek.getDate() + 6);
            return {
              weekNumber: formattedWeekNumber,
              start: new Date(startOfWeek),
              end: new Date(endOfWeek),
              week: weekInfo.week,
              year: weekInfo.year
            };
          });
          setWeeks(tempWeeks);
          if ((selectedWeek === null || selectedWeek === undefined) && tempWeeks.length > 0) {
            setSelectedWeek(tempWeeks[0].weekNumber);
          }
        })
        .catch((err) => {
          console.error("Помилка завантаження тижнів з даними:", err);
          setWeeks([]);
        });
      return;
    }

    const today = new Date();
    
    // Знаходимо поточний тиждень
    const currentWeekInfo = getWeekInfo(today);
    const currentMonday = currentWeekInfo.monday;
    
    console.log('📅 WeekSelector - поточний тиждень:', currentWeekInfo);
    
    // Генеруємо 52 тижні назад від поточного
    const tempWeeks = [];
    let monday = new Date(currentMonday);
    
    for (let i = 0; i < 52; i++) {
      const weekInfo = getWeekInfo(monday);
      const startOfWeek = new Date(weekInfo.monday);
      const endOfWeek = new Date(weekInfo.monday);
      endOfWeek.setDate(endOfWeek.getDate() + 6);
      
      const formattedWeekNumber = `${weekInfo.year}${String(weekInfo.week).padStart(2, '0')}`;
      
      tempWeeks.push({
        weekNumber: formattedWeekNumber,
        start: new Date(startOfWeek),
        end: new Date(endOfWeek),
        week: weekInfo.week,
        year: weekInfo.year
      });
      
      monday.setDate(monday.getDate() - 7);
    }
    
    console.log('📊 WeekSelector - згенеровані тижні:', tempWeeks.length);
    
    setWeeks(tempWeeks);

    if (selectedWeek === null || selectedWeek === undefined) {
      if (tempWeeks.length > 1) {
        const formattedPrevWeek = tempWeeks[1].weekNumber;
        console.log('🎯 WeekSelector - встановлюємо selectedWeek (попередній тиждень):', formattedPrevWeek);
        setSelectedWeek(formattedPrevWeek);
      }
    } else {
      console.log('🎯 WeekSelector - поточний selectedWeek:', selectedWeek);
    }
  }, [selectedWeek, setSelectedWeek, availableWeeksOnly]);

  const formatDate = (date) => {
    return `${String(date.getDate()).padStart(2, "0")}.${String(date.getMonth() + 1).padStart(2, "0")}`;
  };

  const isCompact = className?.includes("h-100");
  const noMargin = className?.includes("mb-0");
  const wrapperClass = `${className || "col-md-3"} ${!isCompact && !noMargin ? "mb-3" : ""}`.trim();
  return (
    <div className={wrapperClass} style={isCompact ? { height: "100%" } : noMargin ? { height: "100%" } : undefined}>
      <select
        className={`form-select ${isCompact ? "h-100" : ""}`}
        style={noMargin ? { height: "100%" } : undefined}
        id="week"
        value={selectedWeek || ""}
        onChange={(e) => {
          console.log('🔄 WeekSelector - зміна selectedWeek:', e.target.value);
          setSelectedWeek(e.target.value);
        }}
      >
        <option value="" disabled>
          -- {t("selectWeek")} --
        </option>
        {weeks.map(({ weekNumber, start, end, week, year }) => (
          <option key={weekNumber} value={weekNumber}>
            {t("week")} {String(week).padStart(2, '0')}.{year} ({formatDate(start)} – {formatDate(end)})
          </option>
        ))}
      </select>
    </div>
  );
};

export default WeekSelector;
